"use client";

import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import AlertDialog from "./AlertDialog";

const API = "https://erp-production-72da01c8e651.herokuapp.com";

const cls = (...c) => c.filter(Boolean).join(" ");
const fmtC = (n) => {
  if (!Number.isFinite(n)) return "$0.00";
  return `$${Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// ── Search engine ─────────────────────────────────────────────────────────────
const ALIASES = {
  dw: ["drywall", "dry wall"],
  gb: ["gypsum board", "gyp board"],
  std: ["stud", "studs"],
  ins: ["insulation"],
  trk: ["track"],
};

function normalise(str) {
  return (str || "")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
function tokenise(str) {
  return normalise(str)
    .split(/[\s\-\/,]+/)
    .filter(Boolean);
}

function scoreItem(item, queryTokens) {
  if (!queryTokens.length) return { score: 0, matched: [] };
  const haystack = [
    item.itemCode,
    item.description,
    item.sectionNumber,
    item.uom,
  ]
    .filter(Boolean)
    .map(normalise)
    .join(" ");
  const haystackTokens = tokenise(haystack);
  let score = 0;
  const matched = [];

  for (const qt of queryTokens) {
    const qtNorm = normalise(qt);
    const code = normalise(item.itemCode || "");
    if (code === qtNorm) {
      score += 100;
      matched.push(qt);
      continue;
    }
    if (code.startsWith(qtNorm)) {
      score += 60;
      matched.push(qt);
      continue;
    }
    const descTokens = tokenise(item.description || "");
    if (descTokens.includes(qtNorm)) {
      score += 30;
      matched.push(qt);
      continue;
    }
    if (haystack.includes(qtNorm)) {
      score += 15;
      matched.push(qt);
      continue;
    }
    let aliasHit = false;
    for (const [canonical, aliases] of Object.entries(ALIASES)) {
      const allForms = [canonical, ...aliases].map(normalise);
      if (
        allForms.includes(qtNorm) &&
        allForms.some((f) => haystack.includes(f))
      ) {
        score += 12;
        matched.push(qt);
        aliasHit = true;
        break;
      }
    }
    if (aliasHit) continue;
    const fuzzy = haystackTokens.find((ht) => {
      if (ht.length < qtNorm.length) return false;
      let qi = 0;
      for (const ch of ht) {
        if (ch === qtNorm[qi]) qi++;
        if (qi === qtNorm.length) return true;
      }
      return false;
    });
    if (fuzzy) {
      score += 5;
      matched.push(qt);
    }
  }
  if (matched.length === queryTokens.length && queryTokens.length > 0)
    score += 20;
  return { score, matched };
}

function Highlight({ text = "", tokens = [] }) {
  if (!tokens.length) return <span>{text}</span>;
  const lower = text.toLowerCase();
  const ranges = [];
  for (const t of tokens) {
    const tn = normalise(t);
    let idx = 0;
    while ((idx = lower.indexOf(tn, idx)) !== -1) {
      ranges.push([idx, idx + tn.length]);
      idx += tn.length;
    }
  }
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const [s, e] of ranges) {
    if (merged.length && s <= merged[merged.length - 1][1])
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
    else merged.push([s, e]);
  }
  const parts = [];
  let cursor = 0;
  for (const [s, e] of merged) {
    if (s > cursor) parts.push(text.slice(cursor, s));
    parts.push(
      <mark
        key={s}
        className="bg-orange-500/30 text-orange-200 rounded-sm px-0.5"
      >
        {text.slice(s, e)}
      </mark>,
    );
    cursor = e;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <span>{parts}</span>;
}

// ── Labour Search Modal ───────────────────────────────────────────────────────
function LabourSearchModal({ open, catalog, onSelect, onClose }) {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const rowRefs = useRef({});

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  const queryTokens = useMemo(() => tokenise(query), [query]);

  const scored = useMemo(() => {
    const list = catalog || [];
    if (!queryTokens.length) {
      return list
        .map((item) => ({ item, score: 0, matched: [] }))
        .sort((a, b) =>
          (a.item.itemCode || "").localeCompare(b.item.itemCode || ""),
        );
    }
    return list
      .map((item) => ({ item, ...scoreItem(item, queryTokens) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score);
  }, [catalog, queryTokens]);

  useEffect(() => setActiveIdx(0), [scored]);

  const scrollTo = (idx) =>
    rowRefs.current[idx]?.scrollIntoView({ block: "nearest" });

  const handleKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => {
        const n = Math.min(i + 1, scored.length - 1);
        scrollTo(n);
        return n;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => {
        const n = Math.max(i - 1, 0);
        scrollTo(n);
        return n;
      });
    } else if (e.key === "Enter" && scored[activeIdx])
      onSelect(scored[activeIdx].item);
    else if (e.key === "Escape") onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70">
      <div
        className="bg-gray-800 rounded-xl border border-gray-700 shadow-2xl flex flex-col w-full max-w-3xl mx-4"
        style={{ maxHeight: "80vh" }}
        onKeyDown={handleKey}
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-700 shrink-0">
          <div>
            <h3 className="text-sm font-semibold text-white">
              Search Labour Items
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Select an item from the database to set a customer rate
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="px-4 pt-3 pb-2 border-b border-gray-700 shrink-0">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
              />
            </svg>
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search code, description, section…"
              className="w-full pl-9 pr-8 py-2 text-xs bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-sm"
              >
                ×
              </button>
            )}
          </div>
          <div className="flex items-center justify-end mt-1.5">
            <span className="text-[10px] text-gray-600">
              {scored.length} of {(catalog || []).length} · ↑↓ navigate · ↵
              select
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-900 sticky top-0 z-10 border-b border-gray-700/70">
              <tr>
                {["Code", "Description", "UOM", "Cost"].map((h, i) => (
                  <th
                    key={h}
                    className={cls(
                      "px-2 py-2.5 text-[10px] text-gray-500 font-medium uppercase tracking-wide",
                      i === 0
                        ? "text-left w-28"
                        : i === 1
                          ? "text-left"
                          : i === 2
                            ? "text-center w-16"
                            : "text-right w-24",
                    )}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {scored.map(({ item }, idx) => {
                const isActive = idx === activeIdx;
                const cost = item.defaultUnitRate || item.defaultCost || 0;
                return (
                  <tr
                    key={item._id || item.itemCode || idx}
                    ref={(el) => {
                      rowRefs.current[idx] = el;
                    }}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => onSelect(item)}
                    className={cls(
                      "cursor-pointer transition-colors",
                      isActive
                        ? "bg-orange-500/10 border-l-2 border-l-orange-500"
                        : idx % 2 === 0
                          ? "bg-gray-900/40 hover:bg-gray-800/60"
                          : "bg-gray-800/20 hover:bg-gray-800/60",
                    )}
                  >
                    <td className="px-2 py-2 font-mono">
                      <span
                        className={cls(
                          "text-[11px] font-medium",
                          isActive ? "text-orange-300" : "text-gray-300",
                        )}
                      >
                        <Highlight
                          text={item.itemCode || "—"}
                          tokens={queryTokens}
                        />
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      <p className="text-gray-100 text-[12px] leading-snug font-medium">
                        <Highlight
                          text={item.description || "—"}
                          tokens={queryTokens}
                        />
                      </p>
                      {item.sectionNumber && (
                        <p className="text-[10px] text-gray-500 mt-0.5">
                          {item.sectionNumber}
                        </p>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-700/60 text-gray-400">
                        {item.uom || "—"}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-gray-300">
                      {fmtC(cost)}
                    </td>
                  </tr>
                );
              })}
              {scored.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="py-12 text-center text-gray-500 text-sm"
                  >
                    {query ? (
                      <span>
                        No items match "{query}"{" "}
                        <button
                          onClick={() => setQuery("")}
                          className="text-orange-400 hover:text-orange-300 underline ml-1"
                        >
                          clear
                        </button>
                      </span>
                    ) : (
                      "No labour items available"
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="shrink-0 border-t border-gray-700 px-4 py-3 flex justify-end">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-gray-300 bg-gray-700 rounded hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Drawer Panel (Add / Edit) ─────────────────────────────────────────────────
function DrawerPanel({
  open,
  editingItem,
  selectedDbItem,
  formData,
  setFormData,
  catalogLoading,
  onOpenSearch,
  onSubmit,
  onCancel,
  onDelete,
  isSaving,
  isDeleting,
}) {
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  // Reset confirm state whenever the drawer closes or switches item
  React.useEffect(() => {
    setConfirmDelete(false);
  }, [open, editingItem?._id]);
  return (
    <>
      {/* Backdrop */}
      <div
        className={cls(
          "fixed inset-0 z-40 bg-black/40 transition-opacity duration-300",
          open
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none",
        )}
        onClick={onCancel}
      />

      {/* Panel */}
      <div
        className={cls(
          "fixed top-0 right-0 h-full w-full max-w-md z-50 bg-gray-800 border-l border-gray-700 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        {/* Panel header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 shrink-0">
          <div>
            <h2 className="text-sm font-semibold text-white">
              {editingItem ? "Edit Customer Rate" : "Add Labour Item"}
            </h2>
            <p className="text-[11px] text-gray-500 mt-0.5">
              {editingItem
                ? "Update the billable rate for this item"
                : "Link an item from the database and set your rate"}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Panel body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* DB Item display / selector */}
          <div>
            <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
              {editingItem ? "Item" : "Item Database"}
            </label>

            {editingItem ? (
              /* Live item info — resolved from ItemDatabase on every GET */
              <div className="rounded-lg bg-gray-900/60 border border-gray-700 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-md bg-orange-500/10 border border-orange-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <svg
                      className="w-4 h-4 text-orange-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0"
                      />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-[11px] font-mono font-medium text-orange-400">
                        {editingItem.itemCode}
                      </p>
                      {editingItem.liveCostResolved ? (
                        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                          live
                        </span>
                      ) : (
                        <span
                          className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20"
                          title="Item code not found in the database — showing stored snapshot"
                        >
                          stale
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-100 font-medium mt-0.5 leading-snug">
                      {editingItem.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-700 text-gray-400">
                        {editingItem.uom}
                      </span>
                      {editingItem.sectionNumber && (
                        <span className="text-[10px] text-gray-500">
                          {editingItem.sectionNumber}
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-600 mt-1.5">
                      Description, UOM &amp; section are read-only — managed in
                      the item database
                    </p>
                  </div>
                </div>
              </div>
            ) : selectedDbItem ? (
              /* Selected item card */
              <div className="rounded-lg bg-gray-900/60 border border-orange-500/30 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[11px] font-mono font-medium text-orange-400">
                      {selectedDbItem.itemCode}
                    </p>
                    <p className="text-sm text-gray-100 font-medium mt-0.5 leading-snug">
                      {selectedDbItem.description}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-700 text-gray-400">
                        {selectedDbItem.uom}
                      </span>
                      {selectedDbItem.sectionNumber && (
                        <span className="text-[10px] text-gray-500">
                          {selectedDbItem.sectionNumber}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500 mt-2">
                      Your cost:{" "}
                      <span className="text-emerald-400 font-medium">
                        {fmtC(selectedDbItem.defaultUnitRate)}
                      </span>
                      <span className="text-gray-600">
                        {" "}
                        · resolved live from database
                      </span>
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onOpenSearch}
                    className="shrink-0 px-2.5 py-1.5 text-[11px] font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 transition-colors"
                  >
                    Change
                  </button>
                </div>
              </div>
            ) : (
              /* Search trigger button */
              <button
                type="button"
                onClick={onOpenSearch}
                disabled={catalogLoading}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-lg bg-gray-900/60 border border-dashed border-gray-600 hover:border-orange-500/50 hover:bg-gray-900 text-gray-400 hover:text-gray-200 transition-all group"
              >
                <div className="w-8 h-8 rounded-md bg-gray-800 border border-gray-700 group-hover:border-orange-500/30 flex items-center justify-center shrink-0 transition-colors">
                  {catalogLoading ? (
                    <svg
                      className="w-4 h-4 animate-spin text-gray-500"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                      />
                    </svg>
                  )}
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium">
                    {catalogLoading
                      ? "Loading catalog…"
                      : "Search labour items"}
                  </p>
                  <p className="text-[11px] text-gray-600 mt-0.5">
                    Pick from the item database to auto-fill code, UOM &amp;
                    section
                  </p>
                </div>
              </button>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-700/60" />

          {/* Customer rate */}
          <div>
            <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
              Customer Rate
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium select-none">
                $
              </span>
              <input
                type="number"
                value={formData.unitRate}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    unitRate: parseFloat(e.target.value) || 0,
                  }))
                }
                className="w-full pl-7 pr-14 py-2.5 rounded-lg bg-gray-900/60 border border-gray-700 text-gray-100 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 text-[11px] font-medium">
                / {editingItem?.uom || selectedDbItem?.uom || "unit"}
              </span>
            </div>
            <p className="text-[11px] text-gray-600 mt-1.5">
              Billed to the customer. Your cost is resolved live from the item
              database.
            </p>
          </div>

          {/* Default project item toggle */}
          <div className="flex items-center justify-between p-3.5 rounded-lg bg-gray-900/40 border border-gray-700/60">
            <div>
              <p className="text-sm font-medium text-gray-200">
                Default project item
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Auto-added to all new projects when enabled
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  isDefaultProjectItem: !prev.isDefaultProjectItem,
                }))
              }
              className={cls(
                "relative w-11 h-6 rounded-full transition-colors duration-200 shrink-0",
                formData.isDefaultProjectItem
                  ? "bg-orange-400"
                  : "bg-gray-700 border border-gray-600",
              )}
            >
              <span
                className={cls(
                  "absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200",
                  formData.isDefaultProjectItem
                    ? "translate-x-5"
                    : "translate-x-0.5",
                )}
              />
            </button>
          </div>
        </div>

        {/* Panel footer */}
        <div className="shrink-0 border-t border-gray-700 px-6 py-4">
          {/* Delete confirm row — only shown when editing and user clicked Delete */}
          {editingItem && confirmDelete && (
            <div className="flex items-center gap-3 mb-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <svg
                className="w-4 h-4 text-red-400 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-xs text-red-300 flex-1">
                Remove this labour item from the catalogue?
              </p>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={isDeleting}
                className="text-xs text-gray-400 hover:text-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onDelete}
                disabled={isDeleting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                {isDeleting && (
                  <svg
                    className="w-3 h-3 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                )}
                {isDeleting ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          )}

          <div className="flex items-center justify-between">
            {/* Delete trigger (edit mode only) */}
            {editingItem && !confirmDelete ? (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                disabled={isSaving || isDeleting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg border border-transparent hover:border-red-500/20 transition-all"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                Delete
              </button>
            ) : (
              <span />
            )}

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onCancel}
                disabled={isSaving || isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onSubmit}
                disabled={
                  isSaving || isDeleting || (!editingItem && !selectedDbItem)
                }
                className={cls(
                  "inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                  isSaving || isDeleting || (!editingItem && !selectedDbItem)
                    ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                    : "bg-orange-400 hover:bg-orange-500 text-gray-900",
                )}
              >
                {isSaving && (
                  <svg
                    className="w-3.5 h-3.5 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                )}
                {isSaving
                  ? "Saving…"
                  : editingItem
                    ? "Update Rate"
                    : "Add Item"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ onAdd }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-14 h-14 rounded-2xl bg-gray-700/60 border border-gray-700 flex items-center justify-center mb-4">
        <svg
          className="w-7 h-7 text-gray-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0"
          />
        </svg>
      </div>
      <p className="text-sm font-medium text-gray-300">No labour items yet</p>
      <p className="text-xs text-gray-600 mt-1 max-w-xs">
        Add items from the item database to manage customer rates for labour.
      </p>
      <button
        onClick={onAdd}
        className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-900 bg-orange-400 hover:bg-orange-500 rounded-lg transition-colors"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 4v16m8-8H4"
          />
        </svg>
        Add first item
      </button>
    </div>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────
export default function LabourItemsView() {
  const [labourItems, setLabourItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [editingLabourItem, setEditingLabourItem] = useState(null);
  const [selectedDbItem, setSelectedDbItem] = useState(null);
  const [formData, setFormData] = useState({
    unitRate: 0.0,
    isDefaultProjectItem: false,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const closeAlert = () => setAlert((a) => ({ ...a, open: false }));

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      await fetchLabourItems();
      setIsLoading(false);
    };
    load();
  }, []);

  const fetchLabourItems = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/labour-items`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLabourItems(res.data);
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to load labour items.",
        onConfirm: closeAlert,
      });
    }
  };

  const fetchCatalog = async () => {
    if (catalog.length > 0) return;
    setCatalogLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/item-database/catalog`, {
        params: { category: "L", isActive: "true" },
        headers: { Authorization: `Bearer ${token}` },
      });
      setCatalog(res.data.items || []);
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to load labour catalog.",
        onConfirm: closeAlert,
      });
    } finally {
      setCatalogLoading(false);
    }
  };

  const openSearch = async () => {
    await fetchCatalog();
    setSearchOpen(true);
  };

  const handleSelectFromCatalog = (dbItem) => {
    setSelectedDbItem({
      value: dbItem._id,
      itemCode: dbItem.itemCode,
      description: dbItem.description,
      uom: dbItem.uom || "EA",
      sectionNumber: dbItem.sectionNumber || "",
      costCode: dbItem.costCode || null,
      defaultUnitRate: dbItem.defaultUnitRate ?? dbItem.defaultCost ?? 0,
    });
    setSearchOpen(false);
  };

  const resetForm = () => {
    setSelectedDbItem(null);
    setFormData((prev) => ({
      unitRate: 0.0,
      isDefaultProjectItem: prev.isDefaultProjectItem,
    }));
  };

  const openAddDrawer = async () => {
    setEditingLabourItem(null);
    resetForm();
    setFormData({ unitRate: 0.0, isDefaultProjectItem: false });
    await fetchCatalog();
    setDrawerOpen(true);
  };

  const openEditDrawer = (item) => {
    setEditingLabourItem(item);
    setSelectedDbItem(null);
    setFormData({
      unitRate: item.unitRate || 0.0,
      isDefaultProjectItem: item.isDefaultProjectItem || false,
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => {
      setEditingLabourItem(null);
      resetForm();
    }, 300);
  };

  const handleSubmit = async () => {
    if (!editingLabourItem && !selectedDbItem) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation Error",
        message:
          "Please search for and select a labour item from the database.",
        onConfirm: closeAlert,
      });
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");
      if (editingLabourItem) {
        await axios.put(
          `${API}/api/labour-items/${editingLabourItem._id}`,
          {
            unitRate: parseFloat(formData.unitRate) || 0,
            isDefaultProjectItem: formData.isDefaultProjectItem,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: "Customer rate updated successfully!",
          onConfirm: closeAlert,
        });
      } else {
        const payload = {
          title: selectedDbItem.description,
          itemCode: selectedDbItem.itemCode,
          uom: selectedDbItem.uom,
          sectionNumber: selectedDbItem.sectionNumber,
          costCode: selectedDbItem.costCode,
          unitRate: parseFloat(formData.unitRate) || 0,
          isDefaultProjectItem: formData.isDefaultProjectItem,
        };
        await axios.post(`${API}/api/labour-items`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: "Labour item added successfully!",
          onConfirm: closeAlert,
        });
      }
      closeDrawer();
      fetchLabourItems();
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to save labour item: " +
          (err.response?.data?.message || err.message),
        onConfirm: closeAlert,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingLabourItem) return;
    setIsDeleting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/api/labour-items/${editingLabourItem._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      closeDrawer();
      fetchLabourItems();
      setAlert({
        open: true,
        type: "success",
        title: "Deleted",
        message: "Labour item removed successfully.",
        onConfirm: closeAlert,
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to delete: " + (err.response?.data?.message || err.message),
        onConfirm: closeAlert,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Derived stats
  const defaultCount = labourItems.filter((i) => i.isDefaultProjectItem).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <svg
          className="w-5 h-5 animate-spin text-gray-600"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      </div>
    );
  }

  return (
    <div className="mt-4 px-4">
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        {/* ── Header ── */}
        <div className="px-6 py-5 border-b border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold text-white tracking-tight">
              Customer Labour Items
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Description, UOM &amp; section are read from the item database ·
              only the customer rate is stored here
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Stats chips */}
            {labourItems.length > 0 && (
              <div className="hidden sm:flex items-center gap-2">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-700 text-gray-400 border border-gray-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
                  {labourItems.length} item{labourItems.length !== 1 ? "s" : ""}
                </span>
                {defaultCount > 0 && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-orange-500/10 text-orange-400 border border-orange-500/20">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                    {defaultCount} default
                  </span>
                )}
              </div>
            )}

            <button
              onClick={openAddDrawer}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-900 bg-orange-400 hover:bg-orange-500 rounded-lg transition-colors shadow-sm whitespace-nowrap"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add Item
            </button>
          </div>
        </div>

        {/* ── Table or Empty ── */}
        {labourItems.length === 0 ? (
          <EmptyState onAdd={openAddDrawer} />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-700/60">
                  <th className="px-6 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-center text-[10px] font-medium text-gray-500 uppercase tracking-wider w-16">
                    UOM
                  </th>
                  <th className="px-6 py-3 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                    Rate
                  </th>
                  <th className="px-6 py-3 text-center text-[10px] font-medium text-gray-500 uppercase tracking-wider w-24">
                    Default
                  </th>
                  <th className="px-6 py-3 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider w-20">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/40">
                {labourItems.map((item) => (
                  <tr
                    key={item._id}
                    onClick={() => openEditDrawer(item)}
                    className="group hover:bg-gray-700/30 transition-colors cursor-pointer"
                  >
                    {/* Code + section */}
                    <td className="px-6 py-4">
                      <span className="font-mono text-[12px] font-medium text-orange-400">
                        {item.itemCode || "—"}
                      </span>
                      {item.sectionNumber && (
                        <p className="text-[10px] text-gray-600 mt-0.5 font-mono">
                          {item.sectionNumber}
                        </p>
                      )}
                    </td>

                    {/* Description — always live from ItemDatabase */}
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-2">
                        <p className="text-sm text-gray-200 font-medium leading-snug">
                          {item.title || "—"}
                        </p>
                        {!item.liveCostResolved && (
                          <span
                            title="Item code not found in the item database — description may be stale"
                            className="mt-0.5 shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20"
                          >
                            ⚠ stale
                          </span>
                        )}
                      </div>
                    </td>

                    {/* UOM — live from ItemDatabase */}
                    <td className="px-6 py-4 text-center">
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-gray-700/60 text-gray-400 border border-gray-700">
                        {item.uom || "—"}
                      </span>
                    </td>

                    {/* Customer rate — the only stored/editable value */}
                    <td className="px-6 py-4 text-right">
                      <span className="text-sm font-semibold text-gray-100 tabular-nums">
                        ${(item.unitRate || 0).toFixed(2)}
                      </span>
                    </td>

                    {/* Default pill */}
                    <td className="px-6 py-4 text-center">
                      {item.isDefaultProjectItem ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-500/10 text-orange-400 border border-orange-500/20">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                          Yes
                        </span>
                      ) : (
                        <span className="text-[10px] text-gray-600">—</span>
                      )}
                    </td>

                    {/* Action */}
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDrawer(item);
                        }}
                        className="text-[11px] font-medium text-gray-500 group-hover:text-orange-400 transition-colors"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Drawer ── */}
      <DrawerPanel
        open={drawerOpen}
        editingItem={editingLabourItem}
        selectedDbItem={selectedDbItem}
        formData={formData}
        setFormData={setFormData}
        catalogLoading={catalogLoading}
        onOpenSearch={openSearch}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        onCancel={closeDrawer}
        isSaving={isSaving}
        isDeleting={isDeleting}
      />

      {/* ── Search Modal ── */}
      <LabourSearchModal
        open={searchOpen}
        catalog={catalog}
        onSelect={handleSelectFromCatalog}
        onClose={() => setSearchOpen(false)}
      />

      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={closeAlert}
        onConfirm={alert.onConfirm}
        confirmText="OK"
        cancelText="Close"
      />
    </div>
  );
}
